/** @jsx jsx */

import { pluralize } from '@zapier/common-utils';
import { Icon, Spacer, Text } from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

const Styles = {
  healthStat: css`
    align-items: center;
    display: flex;
    margin-bottom: 25px;
  `,
};

const healthStatWrapper = css`
  &&&&&& > span {
    text-transform: uppercase;
  }
`;

const HealthStat = ({ children, icon }) => (
  <div css={Styles.healthStat}>
    {icon}
    <Spacer width={20} />
    {children}
  </div>
);

const BugsCount = ({ totalBugs }) =>
  totalBugs ? (
    <Text>
      Fixing{' '}
      <Text fontWeight={600}>
        {totalBugs} {pluralize('bug', totalBugs)}
      </Text>
    </Text>
  ) : (
    <Text>
      <Text fontWeight={600}>Great job!</Text> No open bugs to fix.
    </Text>
  );

const FeatureRequestsCount = ({ totalRequests }) =>
  totalRequests ? (
    <Text>
      Adding{' '}
      <Text fontWeight={600}>
        {totalRequests} open feature {pluralize('requests', totalRequests)}
      </Text>
    </Text>
  ) : (
    <Text>
      <Text fontWeight={600}>Nice work!</Text> No open feature requests.
    </Text>
  );

const IssuesCount = ({ issuesCount, issuesPercentage }) =>
  issuesCount ? (
    <Text>
      Responding to open bugs and feature requests. You have a{' '}
      <Text fontWeight={600}>
        {Math.round(issuesPercentage * 100)}% response rate.
      </Text>
    </Text>
  ) : (
    <Text>
      <Text fontWeight={600}>Woo hoo!</Text> No issues to respond to.
    </Text>
  );

type Props = {
  bugs: number;
  featureRequests: number;
  issues: number;
  percentResponded: number;
};

const HealthStats = (props: Props) => (
  <div css={healthStatWrapper}>
    <Text type="SmallPrint4">Improve Your Integration By:</Text>
    <Spacer height={20} />
    <HealthStat
      icon={
        <Icon
          aria-label="Bugs"
          color="GrayWarm6"
          name="actionBugFill"
          size={30}
        />
      }
    >
      <BugsCount totalBugs={props.bugs} />
    </HealthStat>
    <HealthStat
      icon={
        <Icon
          aria-label="Feature Requests"
          color="GrayWarm6"
          name="miscBolt"
          size={30}
        />
      }
    >
      <FeatureRequestsCount totalRequests={props.featureRequests} />
    </HealthStat>
    <HealthStat
      icon={
        <Icon
          aria-label="Responses"
          color="GrayWarm6"
          name="commentConvoFill"
          size={30}
        />
      }
    >
      <IssuesCount
        issuesCount={props.issues}
        issuesPercentage={props.percentResponded}
      />
    </HealthStat>
  </div>
);

export default HealthStats;
